<template>
  <button
    v-show="isVisible"
    type="button"
    class="back-to-top"
    :class="{
      'back-to-top--inline': inline,
      'back-to-top--product-detail': !inline && isStoreProductDetailPage,
    }"
    :style="buttonStyle"
    aria-label="回到顶部"
    @click.stop="scrollToTop"
  >
    <SvgIcon name="back-top" :size="iconSize" />
  </button>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { throttle } from '@/utils'

const route = useRoute()

const props = defineProps({
  visible: {
    type: Boolean,
    default: undefined,
  },
  inline: {
    type: Boolean,
    default: false,
  },
  threshold: {
    type: Number,
    default: 300,
  },
  size: {
    type: Number,
    default: 40,
  },
  iconSize: {
    type: Number,
    default: 24,
  },
  guard: {
    type: Function,
    default: () => true,
  },
})

const emit = defineEmits(['click'])

const showBackToTop = ref(false)

const isStoreProductDetailPage = computed(() => /^\/store\/products\/[^/]+$/.test(route.path))
const isControlledVisible = computed(() => props.visible !== undefined)
const isVisible = computed(() => (isControlledVisible.value ? props.visible : showBackToTop.value))
const formatCssSize = (size) => (typeof size === 'number' ? `${size}px` : size)
const buttonStyle = computed(() => ({
  '--back-to-top-size': formatCssSize(props.size),
}))

const handleScroll = throttle(() => {
  if (isControlledVisible.value) return

  const shouldShow = window.scrollY > props.threshold
  if (shouldShow !== showBackToTop.value) {
    showBackToTop.value = shouldShow
  }
}, 100)

const scrollToTop = (event) => {
  if (!import.meta.client || !props.guard()) return

  emit('click', event)
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  })
}

onMounted(() => {
  if (!import.meta.client) return
  if (isControlledVisible.value) return

  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll()
})

onUnmounted(() => {
  if (!import.meta.client) return
  if (isControlledVisible.value) return

  window.removeEventListener('scroll', handleScroll)
})
</script>

<style lang="scss" scoped>
.back-to-top {
  position: fixed;
  right: 16px;
  bottom: calc(80px + env(safe-area-inset-bottom, 0px));
  width: var(--back-to-top-size);
  height: var(--back-to-top-size);
  padding: 4px;
  border: 0;
  border-radius: 50%;
  background-color: $bg-white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: $text-title-color;
  box-shadow: 0 3.2px 8px 0 rgba(0, 0, 0, 0.1);
  z-index: $z-index-page-floating;
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  &--product-detail {
    bottom: calc(132px + env(safe-area-inset-bottom, 0px));
  }

  &--inline {
    position: static;
    right: auto;
    bottom: auto;
    flex: 0 0 auto;
    touch-action: none;
  }
}
</style>
